/**
 * AITab - Chat interface for AI-powered research Q&A
 *
 * Features:
 * - Question input with suggestions
 * - Conversation history with markdown support
 * - Source citations
 * - Loading states
 */
import { useState, useRef, useEffect, FormEvent } from 'react'
import ReactMarkdown from 'react-markdown'
import { useConversation, useSuggestions } from '../../hooks/useAIChat'
import { usePortfolioFilter } from '../../contexts/PortfolioFilterContext'
import type { ConversationMessage, SuggestedQuestion, SourceReference } from '../../types/ai-qa'

interface AITabProps {
  symbol: string
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Individual chat message bubble
 */
function MessageBubble({ message }: { message: ConversationMessage }) {
  const isUser = message.role === 'user'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg p-4 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-900'
        }`}
      >
        {/* Message content */}
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </div>
        ) : (
          <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-900 prose-li:text-gray-900 prose-strong:text-gray-900">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}

        {/* Sources (assistant only) */}
        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">Sources:</p>
            <div className="flex flex-wrap gap-1">
              {message.sources.map((source, idx) => (
                <SourceBadge key={idx} source={source} />
              ))}
            </div>
          </div>
        )}

        {/* Token usage (assistant only, subtle) */}
        {!isUser && message.tokensUsed && (
          <div className="mt-2 text-xs text-gray-400">
            {message.tokensUsed.input + message.tokensUsed.output} tokens
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Source citation badge
 */
function SourceBadge({ source }: { source: SourceReference }) {
  const typeColors: Record<string, string> = {
    metrics: 'bg-purple-100 text-purple-700',
    financials: 'bg-green-100 text-green-700',
    sec_filing: 'bg-blue-100 text-blue-700',
    insider: 'bg-orange-100 text-orange-700',
    institutional: 'bg-teal-100 text-teal-700',
    philosophy: 'bg-indigo-100 text-indigo-700',
  }

  const color = typeColors[source.type] || 'bg-gray-100 text-gray-700'

  // Philosophy source has no symbol, just show detail or type
  let label: string
  if (source.type === 'philosophy') {
    label = source.detail || 'philosophy'
  } else {
    label = source.detail
      ? `${source.symbol} ${source.detail}`
      : `${source.symbol} ${source.type}`
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  )
}

/**
 * Typing indicator for loading state
 */
function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-lg p-4">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  )
}

/**
 * Suggested question chip
 */
function SuggestionChip({
  suggestion,
  onClick,
}: {
  suggestion: SuggestedQuestion
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="group text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
    >
      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-700">
        {suggestion.question}
      </p>
      <p className="text-xs text-gray-500 mt-0.5">
        {suggestion.reason}
      </p>
    </button>
  )
}

/**
 * Empty state when no messages
 */
function EmptyState({
  symbol,
  suggestions,
  onSuggestionClick,
  isLoadingSuggestions,
}: {
  symbol: string
  suggestions?: SuggestedQuestion[]
  onSuggestionClick: (question: string) => void
  isLoadingSuggestions: boolean
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Ask about {symbol}
      </h3>
      <p className="text-gray-500 text-center max-w-md mb-6">
        Get AI-powered insights from fundamentals, SEC filings, insider trades, and institutional holdings.
      </p>

      {/* Suggested questions */}
      {isLoadingSuggestions ? (
        <div className="space-y-2 w-full max-w-md">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : suggestions && suggestions.length > 0 ? (
        <div className="space-y-2 w-full max-w-md">
          <p className="text-sm text-gray-500 font-medium mb-2">Try asking:</p>
          {suggestions.map((suggestion, idx) => (
            <SuggestionChip
              key={idx}
              suggestion={suggestion}
              onClick={() => onSuggestionClick(suggestion.question)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2 w-full max-w-md">
          <p className="text-sm text-gray-500 font-medium mb-2">Try asking:</p>
          <SuggestionChip
            suggestion={{
              question: `What are the key risks for ${symbol}?`,
              reason: 'Based on SEC filings',
            }}
            onClick={() => onSuggestionClick(`What are the key risks for ${symbol}?`)}
          />
          <SuggestionChip
            suggestion={{
              question: `Summarize recent insider activity`,
              reason: 'From insider trades data',
            }}
            onClick={() => onSuggestionClick(`Summarize recent insider activity for ${symbol}`)}
          />
          <SuggestionChip
            suggestion={{
              question: `What's the bull case for ${symbol}?`,
              reason: 'Investment analysis',
            }}
            onClick={() => onSuggestionClick(`What's the bull case for ${symbol}?`)}
          />
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function AITab({ symbol }: AITabProps) {
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Get global portfolio filter for philosophy context
  const { selectedPortfolioId } = usePortfolioFilter()

  // Conversation state (includes portfolio for philosophy context)
  const {
    messages,
    isLoading,
    error,
    sendQuestion,
    clearConversation,
    updateSymbols,
  } = useConversation({
    initialSymbols: [symbol],
    portfolioId: selectedPortfolioId,
  })

  // Suggested questions
  const { data: suggestionsData, isLoading: loadingSuggestions } = useSuggestions(symbol)

  // Update symbols when prop changes
  useEffect(() => {
    updateSymbols([symbol])
  }, [symbol, updateSymbols])

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Handle form submission
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const question = inputValue.trim()
    if (!question || isLoading) return

    sendQuestion(question)
    setInputValue('')
  }

  // Handle suggestion click
  const handleSuggestionClick = (question: string) => {
    sendQuestion(question)
  }

  const hasMessages = messages.length > 0

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="font-medium text-gray-900">AI Research Assistant</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Nova 2 Lite</span>
        </div>
        {hasMessages && (
          <button
            onClick={clearConversation}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!hasMessages ? (
          <EmptyState
            symbol={symbol}
            suggestions={suggestionsData?.suggestions}
            onSuggestionClick={handleSuggestionClick}
            isLoadingSuggestions={loadingSuggestions}
          />
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 p-4">
        <form onSubmit={handleSubmit} className="flex space-x-3">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`Ask about ${symbol}...`}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              'Ask'
            )}
          </button>
        </form>
        <p className="mt-2 text-xs text-gray-400">
          Answers are generated from your data. Not financial advice.
        </p>
      </div>
    </div>
  )
}

export default AITab
